import { useQueryClient } from "@tanstack/react-query";
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../hoc/AuthContext";

import { HeaderWebsiteConfig } from "../../../hooks/apiHooks/HeaderHook";
import useIpCountryDefault from "../../../hooks/useIpCountryDefault";
import useWindowToTop from "../../../hooks/useWindowScrollToTop";

import { POLICY_PAGE, POLICY_PRIVACY_PAGE, TERMS_PAGE } from "../../../Routes/RoutesContant";

import { defaultTextColor, textColor } from "../../../Utils/CommonFunctions";
import { CARD_NUMBER, LOGIN, SIGNUP } from "../../../Utils/Constants/Login";
import { getDefaultIpCountry } from "../../../Utils/IpCountry";

import Alert from "../../CommonComponents/Alert";
import Button from '../../CommonComponents/Button';

import Input from "../../Partials/Input";
import CountryCodePhoneNumber from "../../Partials/Input/CountryCodePhoneNumber";
import CreditCardInput, { ErrorMessage, SuccessMessage } from "../../Partials/Input/CreditCardInput";
import EmailInputBox from "../../Partials/Input/EmailInputBox";
import OTPInput from "../../Partials/Input/OTPInput";
import FullPageLoader from '../../Partials/Loading/FullPageLoader';
import Spinner from '../../Partials/Loading/Spinner';

import {
  closeAuthForm, handleBinInputChange, handleChangeNumber, handleEnterKey, handleResendOTP, onSubmit, switchToLogin, switchToSignUp
} from "./LoginFunction";

import "./Login.scss";

export default function Login(props) {
  const {
    setShowLoginForm = () => {},
    initialMode = LOGIN,
    showLoginToggle = true,
    showSignUpToggle = true,
    isAuthPageMode = false,
    UId = ""
  } = props;
  
  const [authMode, setAuthMode] = useState(initialMode);

  const { 
    login, setShowLogin, cardInfo, setSubVarientIdGlobal, setSelectedCardData, setWindowLocationUrl, updateCreditCard,
    windowLocationUrl, setIsAuthenticated, setShowSignUp, setCardInfo, setUserData, setShowPasskeyModal, setShowNameModal, setShowWhatsAppOptIn
  } = useAuth();
  
  const queryClient = useQueryClient();
  
  const integratorTheme = queryClient.getQueryData(['integrator-theme', true]) || {};
  
  const { headerConfigData } = HeaderWebsiteConfig();
  const websiteHeaderInfo = headerConfigData?.Header || {};
  const signupFields = websiteHeaderInfo?.signup_fields || [];
  const hasUidField = signupFields.includes("UID");
  const hasNameField = signupFields.includes("NAME");
  const hasBinField = signupFields.includes("BIN");
  const supportsUidSignUp = hasUidField;
  const requireUid = hasUidField && !hasBinField;
  const passkeyPopupshow = websiteHeaderInfo?.is_passkey_auth_present || false;
  const navigate = useNavigate();
  
  const actualUrl = window?.location?.origin;
  const isSignUp = authMode === SIGNUP;
  const isLogin = authMode === LOGIN;

  const [otpValidate, setOtpValidate] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const ipCountryDefault = useIpCountryDefault();
  const countryCodeTouchedRef = useRef(false);
  const [countryCode, setCountryCode] = useState(() => getDefaultIpCountry().dialCode);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [resendNumber, setResendNumber] = useState(false);
  const [requireEmail, setRequireEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [hasEmailError, setHasEmailError] = useState(false);
  const [withoutBinSignUp, setWithoutBinSignUp] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [otpValidateInput, setOtpValidateInput] = useState("");
  const [BinMessage, setBinMessage] = useState("");
  const [BinError, setBinError] = useState("");
  const [phoneMessage, setPhoneMessage] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  const [userId, setUserId] = useState(UId);
  const scrollToTop = useWindowToTop(0, 1000);
  
  const currentMessage = isLogin ? message : phoneMessage;
  const currentError = isLogin ? error : phoneError;
  const formValidationError = formErrors.mobile_number;

  useEffect(() => { scrollToTop() }, [scrollToTop]);
  useEffect(() => { setAuthMode(initialMode) }, [initialMode]);

  useEffect(() => {
    if (!countryCodeTouchedRef.current && !phoneNumber && !otpValidate && !resendNumber) {
      setCountryCode(ipCountryDefault.dialCode);
    }
  }, [ipCountryDefault.dialCode, otpValidate, phoneNumber, resendNumber]);

  useEffect(() => {
    const storedCardNumber = localStorage.getItem(CARD_NUMBER);
    if (storedCardNumber || UId) {
      setWithoutBinSignUp(true);
      if (isSignUp && !UId) {
        setOtpValidateInput(storedCardNumber);
        setCardNumber(storedCardNumber);
      }
      if (UId) { setUserId(UId); }
    }
  }, [authMode, isSignUp, UId]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0 && otpSent) {
      interval = setInterval(() => {
        setResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer, otpSent]);

  useEffect(() => {
    if (isSignUp && cardNumber?.length === 8 && cardNumber !== '') {
      handleBinInputChange({cardNumber, setBinMessage, setBinError, setRequireEmail, setOtpValidateInput});
    }
  }, [cardNumber, authMode, isSignUp]);

  useEffect(() => {
    setOtpValidate(false);
    setResendNumber(false);
    setError("");
    setMessage("");
    setPhoneError("");
    setPhoneMessage("");
    setBinError("");
    setBinMessage("");
    setFormErrors({});
    setName("");
    setPhoneNumber("");
    countryCodeTouchedRef.current = false;
    setCountryCode(ipCountryDefault.dialCode);
    setEmail("");
    setHasEmailError(false);
    setOtp("");
    if(!UId) setUserId("");
  }, [authMode, ipCountryDefault.dialCode]);

  // render footer
  function renderFooter () {
    return(
      <div className="loginFooter">
        {(showSignUpToggle && isLogin) && (
          <>
            New User ? {" "}
            <span className="loginToogleLink"
              onClick={() => switchToSignUp({ setAuthMode, setShowLogin, setShowSignUp, setResendTimer, isAuthPageMode })}
            >
              Sign Up
            </span>
          </>
        )}

        {(showLoginToggle && isSignUp) && (
          <>
            Already a user?{" "}
            <span className="loginToogleLink"
              onClick={() => switchToLogin({ setAuthMode, setShowLogin, setShowSignUp, setResendTimer, isAuthPageMode })}
            >
              Sign In
            </span>
          </>
        )}
      </div>
    );
  };

  // render terms and conditions
  function renderTermsAndCondition() {
    return(
      <div className="termsAndConditions">
        By continuing I agree to the{" "}
        <span
          className="termsLink"
          onClick={() => { closeAuthForm({setShowLoginForm, setShowLogin, setShowSignUp, isAuthPageMode}); 
          navigate(`/${POLICY_PAGE}/${TERMS_PAGE}`)}}
        >T&C</span>{" "}
        and{" "}
        <span
          className="termsLink"
          onClick={() => { closeAuthForm({setShowLoginForm, setShowLogin, setShowSignUp, isAuthPageMode}); 
          navigate(`/${POLICY_PAGE}/${POLICY_PRIVACY_PAGE}`)}}
        >privacy policy</span>
      </div>
    );
  };

  return (
    <>
     <Alert
        message={alert.message}
        show={alert.show}
        setShowAlert={setAlert}
        type={alert.type} // 'success', 'warning', 'error', or 'info'
      />
      <FullPageLoader isLoading={loading} />
      <div className="loginWrapper" style={{ "--theme-default-text-color": defaultTextColor(integratorTheme) }}>
        <main className="loginContainer">
          <header className="loginHeader">
            <div className="loginTitle">
              {websiteHeaderInfo?.auth_title || "Welcome Back!"}
            </div>
            <div className="loginSubtitle">
              {websiteHeaderInfo?.auth_subtitle || "Access exclusive rewards, privileges, and experiences"}
            </div>
          </header>

          <main className="loginContent">
            <div className="loginForm"
              onKeyDown={(e) => handleEnterKey({
                e, isSignUp, name, phoneNumber, email, otp, isLogin, otpValidate, setFormErrors, resendNumber, setLoading, 
                setMessage, setError, setPhoneMessage, setPhoneError, setIsAuthenticated, login, countryCode, 
                setSubVarientIdGlobal, setSelectedCardData, updateCreditCard, setUserData, 
                closeAuthForm, navigate, actualUrl, setWindowLocationUrl, windowLocationUrl, cardInfo, setCardInfo, 
                otpValidateInput, setBinError, setBinMessage, setOtpSent, setResendTimer, setOtpValidate, setResendNumber, 
                setShowLoginForm, setShowLogin, setShowSignUp, withoutBinSignUp, setOtpValidateInput, setAlert,
                setShowPasskeyModal, userId, isAuthPageMode, supportsUidSignUp, requireUid, requireName: hasNameField,
                setShowNameModal, passkeyPopupshow, setShowWhatsAppOptIn, setOtp
              })}
            >
              {/* UID input field when the signup flow supports UID */}
              {isSignUp && hasUidField && (
                <div className="formField uidField">
                  <label htmlFor="UID">UID <span>*</span></label>
                  <Input
                    value={userId}
                    setValue={(val) => {
                      const filtered = val.replace(/[^a-zA-Z0-9]/g, '');
                      setUserId(filtered);
                    }}
                    placeholder="Enter your UID"
                    hasError={!!formErrors.userId}
                    errorMessage={formErrors.userId}
                    className="fieldInput"
                    disabled={!!UId} 
                    id="UID"
                  />
                </div>
              )}

              {/* Full name input field for signup */}
              {isSignUp && hasNameField && (
                <div className="formField nameField">
                  <label htmlFor="fullName">Full Name <span>*</span></label>
                  <Input
                    value={name}
                    setValue={setName}
                    placeholder="Full Name"
                    hasError={!!formErrors.name}
                    errorMessage={formErrors.name}
                    className="fieldInput"
                    disabled={!withoutBinSignUp && otpValidate}
                    id="fullName"
                  />
                </div>
              )}

              {/* Card number input field for signup and with bin number signup */}
              {isSignUp && !withoutBinSignUp && hasBinField && (
                <div className="cardNumberSection">
                  <div className="cardNumberLabel">Please enter your 8 digit card number</div>
                  <CreditCardInput setCardNumber={setCardNumber} />
                  <SuccessMessage message={BinMessage} />
                  <ErrorMessage error={BinError} message={BinMessage} />
                </div>
              )}

              {/* Phone number input field with country selection */}
              <div className={`formField phoneField ${withoutBinSignUp && isSignUp ? "" : ""}`}>
                <label htmlFor="phoneNumber">Phone Number <span>*</span></label>
                <CountryCodePhoneNumber
                  value={phoneNumber}
                  setValue={setPhoneNumber}
                  setCountryCode={(value) => {
                    countryCodeTouchedRef.current = true;
                    setCountryCode(value);
                  }}
                  setRequireEmail={setRequireEmail}
                  setEmail={setEmail}
                  resendNumber={resendNumber}
                  handleChangeNumber={() => handleChangeNumber({
                    setResendNumber, setOtpValidate, isLogin, setMessage, setError, setPhoneMessage, setPhoneError, setOtp
                  })}
                  otpValidate={otpValidate}
                  currentError={currentError}
                  currentMessage={currentMessage}
                  formValidationError={formValidationError}
                  countryCode={countryCode}
                  id="phoneNumber"
                />
              </div>

              {/* Email input field if require email is true or other country then india */}
              {requireEmail && (
                <div className="formField emailField">
                  <label htmlFor="emailAddress">Email Address <span>*</span></label>
                  <EmailInputBox
                    email={email}
                    setEmail={setEmail}
                    hasEmailError={hasEmailError}
                    setHasEmailError={setHasEmailError}
                    placeholder="Email Address"
                    disabled={otpValidate}
                    showIcon={false}
                    id="emailAddress"
                  />
                  {formErrors.email && <ErrorMessage error={formErrors.email} />}
                </div>
              )}

              {/* OTP input field if otp validate is true */}
              {otpValidate && (
                <div className="formField otpField">
                  <label htmlFor="otpInput">One Time Password (OTP) <span>*</span></label>
                  <OTPInput
                    formData={{ otp }}
                    handleInputChange={(e) => setOtp(e.target.value)}
                    errors={formErrors}
                    otp={otp}
                    resendTimer={resendTimer}
                    otpSent={otpSent}
                    id="otpInput"
                    handleResendOTP={(e) => handleResendOTP({e, isLogin, setMessage, setError, setPhoneMessage, setPhoneError, 
                      setLoading, countryCode, email, setOtpSent, setResendTimer, phoneNumber, setOtpValidate, setResendNumber })}
                  />
                  {formErrors.otp && <ErrorMessage error={formErrors.otp} />}
                </div>
              )}

              {/* render terms and conditions */}
              {renderTermsAndCondition()}

              {/* submit button */}
              <Button type="submit" variant="primary" size="full" loading={loading} className="submitButton" disabled={loading}
                theme={integratorTheme}
                onClick={(e) => onSubmit({
                e, isSignUp, name, phoneNumber, email, otp, isLogin, otpValidate, setFormErrors, resendNumber, setLoading, 
                setMessage, setError, setPhoneMessage, setPhoneError, setIsAuthenticated, login, countryCode, 
                setSubVarientIdGlobal, setSelectedCardData, updateCreditCard, setUserData, 
                closeAuthForm, navigate, actualUrl, setWindowLocationUrl, windowLocationUrl, cardInfo, setCardInfo, 
                otpValidateInput, setBinError, setBinMessage, setOtpSent, setResendTimer, setOtpValidate, setResendNumber, 
                setShowLoginForm, setShowLogin, setShowSignUp, withoutBinSignUp, setOtpValidateInput, setAlert,
                setShowPasskeyModal, isAuthPageMode, supportsUidSignUp, requireUid, requireName: hasNameField, userId,
                setShowNameModal, passkeyPopupshow, setShowWhatsAppOptIn, setOtp
              })}
              >
                {loading ? <Spinner color={textColor(integratorTheme)} /> : !otpValidate ? `Send OTP` : isLogin ? "Sign In" : "Sign Up"}
              </Button>
            </div>
          </main>

          {/* render login footer */}
          {renderFooter()}
        </main>
      </div>
    </>
  );
}

Login.propTypes = {
  setShowLoginForm: PropTypes.func.isRequired,
  initialMode: PropTypes.oneOf([LOGIN, SIGNUP]),
  showLoginToggle: PropTypes.bool,
  showSignUpToggle: PropTypes.bool,
  isAuthPageMode: PropTypes.bool,
  UId: PropTypes.string,
};
